import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Home, LayoutDashboard, Handshake, CreditCard, Send,
  Bot, FileSearch, PenTool, ImagePlay, Share2, Inbox, Network,
  BarChart3, TrendingUp, DollarSign, Scale, Users, Target,
  Settings, Lock, Zap, LogOut, ChevronRight, History,
} from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import { useAuth } from '@/hooks/useAuth';
import { TIER_LEVELS } from '@/lib/constants';
import type { SubscriptionTier } from '@/types/common';
import styles from './layout.module.css';

interface NavItem { label: string; icon: React.ReactNode; to: string; requiredTier?: SubscriptionTier; badge?: string; }
interface NavGroup { title: string; items: NavItem[]; }

// Tier ordering is shared knowledge — source it from constants (TIER_LEVELS)
// rather than a local copy, so a tier rename/addition stays consistent with
// the gate hook (useTierGate) and the server. TIER_LABELS below stays local:
// it is a sidebar-specific *display* map (e.g. 'Pro+'), not gate knowledge.
const TIER_LABELS: Record<string, string> = { pro: 'Pro+', manager: 'Manager' };

const NAV_GROUPS: NavGroup[] = [
  { title: 'MAIN', items: [
    { label: 'Home', icon: <Home size={18} />, to: '/' },
    { label: 'Dashboard', icon: <LayoutDashboard size={18} />, to: '/dashboard' },
    { label: 'Deals', icon: <Handshake size={18} />, to: '/deals' },
    { label: 'Affiliate', icon: <Network size={18} />, to: '/affiliate' },
    { label: 'Payments', icon: <CreditCard size={18} />, to: '/payments' },
  ]},
  { title: 'AI TOOLS', items: [
    { label: 'Deal Reviewer', icon: <Bot size={18} />, to: '/deal-reviewer', requiredTier: 'pro', badge: 'Pro+' },
    { label: 'Contract Analyzer', icon: <FileSearch size={18} />, to: '/contract-analyzer', requiredTier: 'pro', badge: 'Pro+' },
    { label: 'Brand Outreach', icon: <Send size={18} />, to: '/outreach', requiredTier: 'manager', badge: 'Manager' },
    { label: 'Industry Intel', icon: <TrendingUp size={18} />, to: '/industry-intel', requiredTier: 'pro', badge: 'Pro+' },
    { label: 'AI History', icon: <History size={18} />, to: '/ai-history' },
  ]},
  { title: 'BUSINESS', items: [
    { label: 'Content Lab', icon: <Share2 size={18} />, to: '/social' },
    { label: 'Hub', icon: <Inbox size={18} />, to: '/hub' },
    { label: 'Media Kit', icon: <ImagePlay size={18} />, to: '/media-kit' },
    { label: 'Finances', icon: <DollarSign size={18} />, to: '/finances' },
    { label: 'Legal & Rights', icon: <Scale size={18} />, to: '/legal', requiredTier: 'manager', badge: 'Manager' },
    { label: 'Collabs', icon: <Users size={18} />, to: '/collabs', requiredTier: 'manager', badge: 'Manager' },
  ]},
  { title: 'ACCOUNT', items: [
    { label: 'My Goals', icon: <Target size={18} />, to: '/goals' },
    { label: 'Settings', icon: <Settings size={18} />, to: '/settings' },
  ]},
];

function tierHasAccess(userTier: SubscriptionTier, requiredTier: SubscriptionTier): boolean {
  return TIER_LEVELS[userTier] >= TIER_LEVELS[requiredTier];
}

const SidebarNavItem: React.FC<{ item: NavItem; userTier: SubscriptionTier }> = ({ item, userTier }) => {
  const hasAccess = !item.requiredTier || tierHasAccess(userTier, item.requiredTier);

  if (!hasAccess) {
    return (
      <div className={`${styles.navItem} ${styles.navItemLocked}`} title={`Requires ${TIER_LABELS[item.requiredTier!] ?? item.requiredTier} plan`}>
        <span className={styles.navItemIcon}>{item.icon}</span>
        <span className={styles.navItemLabel}>{item.label}</span>
        <Lock size={12} className={styles.navItemLockIcon} />
        {item.badge && <span className={styles.navItemBadge}>{item.badge}</span>}
      </div>
    );
  }

  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      className={({ isActive }) => `${styles.navItem} ${isActive ? styles.navItemActive : ''}`}
    >
      {({ isActive }) => (
        <>
          <span className={styles.navItemIcon} style={isActive ? { color: '#00D26A' } : undefined}>{item.icon}</span>
          <span className={styles.navItemLabel}>{item.label}</span>
          {item.badge && tierHasAccess(userTier, item.requiredTier!) && <span className={styles.navItemBadge}>{item.badge}</span>}
        </>
      )}
    </NavLink>
  );
};

export const Sidebar: React.FC = () => {
  const { profile } = useAuthStore();
  const { setSidebarOpen } = useUIStore();
  const { signOut } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const userTier: SubscriptionTier = profile?.subscription_tier ?? 'starter';

  // Primary logout path (sidebar "Sign Out" button). Tear down ALL client
  // state for the prior user before routing to /login, so the next account
  // logging in on the same tab cannot see leftover identity or warm cache:
  //   - useAuth().signOut(): auth.signOut() + clearTokens() + authStore.reset()
  //     (reset() also calls identify(null), clearing the GA4 user_id).
  //   - queryClient.clear(): drop every warm TanStack query (deals, brands,
  //     invoices, …) so none survive into the next session's stale window.
  const handleSignOut = async () => {
    await signOut();
    queryClient.clear();
    navigate('/login');
  };

  return (
    <aside className={styles.sidebar}>
      <div className={styles.sidebarLogo}>
        <div className={styles.sidebarLogoIcon}><Zap size={16} /></div>
        <div className={styles.sidebarLogoText}>
          <span className={styles.sidebarLogoName}>CROS</span>
          <span className={styles.sidebarLogoSub}>Creator Rep OS</span>
        </div>
        <button onClick={() => setSidebarOpen(false)} className={styles.sidebarCloseBtn} aria-label="Close sidebar" style={{ display: 'none' }}>
          <ChevronRight size={16} />
        </button>
      </div>

      {profile && (
        <div className={styles.sidebarUser}>
          <div className={styles.sidebarAvatar}>{profile.full_name?.charAt(0) ?? 'U'}</div>
          <div className={styles.sidebarUserInfo}>
            <p className={styles.sidebarUserName}>{profile.display_name ?? profile.full_name}</p>
            {/* Subtitle only renders for active subscribers — inactive users
                (never-paid OR post-cancel) see the name alone per the locked
                product model. */}
            {profile.subscription_status === 'active' && (
              <p className={styles.sidebarUserTier}>{userTier} plan</p>
            )}
          </div>
        </div>
      )}

      <nav className={styles.sidebarNav} aria-label="Primary">
        {NAV_GROUPS.map((group) => (
          <div key={group.title} className={styles.navGroup}>
            <p className={styles.navGroupTitle}>{group.title}</p>
            {group.items.map((item) => (
              <SidebarNavItem key={item.to} item={item} userTier={userTier} />
            ))}
          </div>
        ))}
      </nav>

      <div className={styles.sidebarFooter}>
        <button onClick={handleSignOut} className={styles.signOutBtn}>
          <LogOut size={16} />
          <span>Sign Out</span>
        </button>
      </div>
    </aside>
  );
};
